import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Bandage, UserPlus } from 'lucide-react';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(email, password, fullName, invitationCode);
      toast.success('Compte créé avec succès');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Bandage className="h-12 w-12 text-blue-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">AUTO-RÉPUTATION</h1>
          <p className="text-gray-400 text-sm">Prenez le contrôle de votre réputation numérique</p>
        </div>

        <Card data-testid="register-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Créer un compte
            </CardTitle>
            <CardDescription>Rejoignez le service confidentiel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Nom complet</Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="Jean Dupont"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  data-testid="fullname-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <p className="text-xs text-gray-500">Minimum 6 caractères</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invitationCode">Code d'invitation *</Label>
                <Input
                  id="invitationCode"
                  type="text"
                  placeholder="INVITE-XXXX-XXXX"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                  required
                  data-testid="invitation-code-input"
                />
                <p className="text-xs text-gray-500">
                  Un code d'invitation est requis pour créer un compte
                </p>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
                data-testid="register-button"
              >
                {loading ? 'Création...' : 'Créer mon compte'}
              </Button>

              <div className="text-center text-sm">
                <span className="text-gray-500">Déjà un compte ? </span>
                <Link to="/login" className="text-blue-600 hover:underline" data-testid="login-link">
                  Se connecter
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-xs text-gray-500">
          <p>En créant un compte, vous acceptez nos conditions d'utilisation</p>
          <p className="mt-2">🔒 Vos données sont chiffrées et confidentielles</p>
        </div>
      </div>
    </div>
  );
};

export default Register;
